"use client";

// Правая панель отдела — «Приложения»: встроенные ссылки на Figma/Miro/Google.
// Раньше тут жили модули отдела; по правке Олега 21.07 модули уехали в верхний
// бар вкладок, а панель отдана приложениям.
//
// Три группы видимости — «каждый видит только что ему нужно»: Общие (весь портал),
// Отдел, Мои. Свёрнутая полоска показывает значки, развёрнутая — списки с
// заголовками групп. Открывается приложение в рабочей области отдела.

import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Check, ExternalLink, Maximize2, Pencil, Plus, Trash2 } from "lucide-react";

import { api } from "@/lib/api";
import { IconSpinner } from "@/components/ui/icons";
import { Toggle } from "@/components/ui/toggle";
import { confirmDialog } from "@/components/ui/confirm";
import {
  SCOPE_HINT, SCOPE_TITLE, appTabKey,
  type AppEmbed, type AppEmbedScope,
} from "@/lib/app-embeds";
import { Button } from "@/components/ui/button";
import { Empty } from "@/components/ui/states";
import { Z } from "@/lib/z";

/** Цвет-акцент провайдера — чтобы значки читались как в остальном портале. */
export const PROVIDER_COLOR: Record<string, string> = {
  figma: "#F24E1E",
  miro: "#FFD02F",
  google_docs: "#4285F4",
  google_sheets: "#0F9D58",
  google_slides: "#F4B400",
  google_forms: "#7248B9",
  notion_site: "#8B5CF6",
  youtube: "#FF0000",
  embed: "#64748B",
};

const initial = (t: string) => (t.trim()[0] || "?").toUpperCase();

/** Значок приложения: первая буква названия на плашке цвета сервиса. */
export function AppEmbedIcon({ item, size = 28 }: { item: AppEmbed; size?: number }) {
  const c = PROVIDER_COLOR[item.provider] ?? PROVIDER_COLOR.embed;
  return (
    <span
      className="flex shrink-0 items-center justify-center rounded-[var(--radius-md)] font-bold"
      style={{
        width: size, height: size, fontSize: Math.round(size * 0.44),
        color: c, background: `color-mix(in srgb, ${c} 16%, transparent)`,
        border: `1px solid color-mix(in srgb, ${c} 40%, transparent)`,
      }}
      data-tooltip={item.provider_title}
    >
      {initial(item.title)}
    </span>
  );
}

export function AppEmbedsPanel({
  items, loading, collapsed, deptKey, activeKey, onSelect, onChanged, canAddToDept, canAddGlobal,
}: {
  items: AppEmbed[];
  loading: boolean;
  collapsed: boolean;
  deptKey: string;
  activeKey: string | null;
  onSelect: (key: string) => void;
  onChanged: () => void;
  canAddToDept: boolean;
  canAddGlobal: boolean;
}) {
  // adding/editing держат КООРДИНАТЫ клика — окно открывается попапом у курсора.
  const [adding, setAdding] = useState<{ x: number; y: number } | null>(null);
  const [editing, setEditing] = useState<{ it: AppEmbed; x: number; y: number } | null>(null);
  // Своё контекстное меню по правому клику на значке (вместо браузерного).
  const [ctx, setCtx] = useState<{ it: AppEmbed; x: number; y: number } | null>(null);
  const openCtx = (e: React.MouseEvent, it: AppEmbed) => {
    e.preventDefault();
    e.stopPropagation();
    setCtx({ it, x: e.clientX, y: e.clientY });
  };
  const deleteItem = async (it: AppEmbed) => {
    const ok = await confirmDialog({
      title: "Удалить приложение?",
      text: `«${it.title}» — ссылка пропадёт с панели отдела.`,
      confirmText: "Удалить",
      danger: true,
    });
    if (!ok) return;
    await api.deleteAppEmbed(it.id);
    onChanged();
  };
  /* Пилюля-подпись свёрнутой полоски. Держим координаты значка и рисуем ПОРТАЛОМ
     в body: полоска прокручиваемая (17 приложений не влезают на 720px), а
     absolute-подсказку внутри скролл-контейнера обрезало бы. */
  const [hint, setHint] = useState<{ it: AppEmbed; top: number; right: number } | null>(null);

  const groups = useMemo(() => {
    const by: Record<AppEmbedScope, AppEmbed[]> = { global: [], department: [], user: [] };
    for (const it of items) by[it.scope]?.push(it);
    return by;
  }, [items]);

  if (collapsed) {
    // ⚠️ Свёрнутый вид повторяет ПЛОТНОСТЬ и СТРУКТУРУ развёрнутого (те же группы,
    // разделители вместо заголовков, тот же вертикальный шаг значка = высоте
    // строки), чтобы при разворачивании высота панели не прыгала (правка Олега
    // 22.07: «момент разворота режет глаз»). Значок 22 + py-1 + space-y-0.5 = как
    // строка списка; разделитель между группами ≈ высота заголовка «ОБЩИЕ».
    const orderedGroups = (["global", "department", "user"] as AppEmbedScope[])
      .map((sc) => groups[sc])
      .filter((l) => l.length);
    // Значки и кнопка «+» — ПРЯМЫЕ flex-дети с justify-between: лишняя высота (рамка
    // = развёрнутой) растекается РАВНОМЕРНО между всеми значками, а не собирается в
    // одну пустоту внизу (правка Олега 22.07). Разделители групп — тоже дети.
    const flat: React.ReactNode[] = [];
    orderedGroups.forEach((list, gi) => {
      if (gi > 0) flat.push(<div key={`sep-${gi}`} className="mx-auto h-px w-5 bg-[color-mix(in_srgb,var(--text)_12%,transparent)]" />);
      list.forEach((it) => flat.push(
        <button
          key={it.id}
          type="button"
          aria-label={`${it.title} · ${it.provider_title}`}
          onClick={() => onSelect(appTabKey(it.id))}
          onMouseEnter={(e) => {
            const r = (e.currentTarget.firstElementChild ?? e.currentTarget).getBoundingClientRect();
            setHint({ it, top: r.top + r.height / 2, right: window.innerWidth - r.right - 6 });
          }}
          onMouseLeave={() => setHint((h) => (h?.it.id === it.id ? null : h))}
          onContextMenu={(e) => openCtx(e, it)}
          className={`flex w-full shrink-0 items-center justify-center rounded-[var(--radius-md)] py-1 transition glass-hover ${
            activeKey === appTabKey(it.id) ? "bg-[color-mix(in_srgb,var(--primary)_12%,transparent)]" : ""
          }`}
        >
          <AppEmbedIcon item={it} size={22} />
        </button>,
      ));
    });
    return (
      <div className="flex h-full flex-col items-stretch justify-between py-1">
        {flat}
        <button
          type="button"
          data-tooltip="Добавить приложение"
          onClick={(e) => setAdding({ x: e.clientX, y: e.clientY })}
          className="flex h-8 w-full shrink-0 items-center justify-center rounded-[var(--radius-md)] text-[var(--textMuted)] transition glass-flat border border-dashed border-[var(--border)]"
        >
          <Plus size={14} />
        </button>
        {hint && <AppEmbedHint {...hint} />}
        {ctx && (
          <AppEmbedContextMenu ctx={ctx} onClose={() => setCtx(null)}
            onOpen={() => onSelect(appTabKey(ctx.it.id))}
            onEdit={ctx.it.can_edit ? () => setEditing({ it: ctx.it, x: ctx.x, y: ctx.y }) : undefined}
            onDelete={ctx.it.can_edit ? () => deleteItem(ctx.it) : undefined} />
        )}
        {adding && (
          <AddAppModal anchor={adding} deptKey={deptKey} canAddToDept={canAddToDept} canAddGlobal={canAddGlobal}
            onClose={() => setAdding(null)} onCreated={() => { setAdding(null); onChanged(); }} />
        )}
        {/* Окно правки — и в свёрнутом виде: контекстное меню зовёт setEditing,
            без этого рендера «Изменить» ничего не делало (правка Олега 22.07). */}
        {editing && (
          <AddAppModal anchor={editing} edit={editing.it} deptKey={deptKey} canAddToDept={canAddToDept} canAddGlobal={canAddGlobal}
            onClose={() => setEditing(null)} onCreated={() => { setEditing(null); onChanged(); }} />
        )}
      </div>
    );
  }

  const renderGroup = (scope: AppEmbedScope) => {
    const list = groups[scope];
    if (!list.length) return null;
    return (
      <div key={scope} className="mb-2">
        <div className="field-label px-2 pb-1" data-tooltip={SCOPE_HINT[scope]}>
          {SCOPE_TITLE[scope]}
        </div>
        <div className="space-y-0.5">
          {list.map((it) => (
            <div key={it.id} className="group/row relative flex items-center gap-1" onContextMenu={(e) => openCtx(e, it)}>
              <button
                type="button"
                onClick={() => onSelect(appTabKey(it.id))}
                data-tooltip={`${it.title} · ${it.provider_title}`}
                className={`flex min-w-0 flex-1 items-center gap-1 rounded-[var(--radius-md)] px-1 py-1 text-left transition glass-hover ${
                  activeKey === appTabKey(it.id) ? "bg-[color-mix(in_srgb,var(--primary)_12%,transparent)]" : ""
                }`}
              >
                <AppEmbedIcon item={it} size={22} />
                {/* Названия длинные («Разработка продукта (fushion)» — 200px при
                    доступных 165), одним уменьшением шрифта не обойтись: даём
                    перенос на вторую строку, обрезаем только совсем длинное. */}
                <span className="min-w-0 flex-1">
                  <span className="line-clamp-2 text-[11px] font-semibold leading-[1.2] text-[var(--text)]">{it.title}</span>
                  <span className="block truncate text-[8.5px] leading-[1.2] text-[var(--textMuted)]">{it.provider_title}</span>
                </span>
              </button>
              {/* Правка/удаление — по правому клику (контекстное меню). Раньше тут
                  были всплывающие карандаш/корзина, Олег убрал их 22.07: дублируют
                  меню и жмутся в узкой панели. На тач меню открывается long-press. */}
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="p-2">
      {loading ? (
        <div className="flex items-center justify-center gap-2 py-6 text-[11px] text-[var(--textMuted)]">
          <IconSpinner size={14} className="animate-spin" /> Загрузка…
        </div>
      ) : items.length === 0 ? (
        <Empty compact title="Пока пусто" text="Добавьте файл из Figma, доску Miro или Google-документ — он откроется прямо здесь, без переключения вкладок браузера." />
      ) : (
        <>{(["global", "department", "user"] as AppEmbedScope[]).map(renderGroup)}</>
      )}

      <Button title="Добавить приложение" className="w-full mt-1 justify-center" onClick={(e) => setAdding({ x: e.clientX, y: e.clientY })}>
        <Plus size={13} /> Добавить
      </Button>

      {adding && (
        <AddAppModal anchor={adding} deptKey={deptKey} canAddToDept={canAddToDept} canAddGlobal={canAddGlobal}
          onClose={() => setAdding(null)} onCreated={() => { setAdding(null); onChanged(); }} />
      )}
      {editing && (
        <AddAppModal anchor={editing} edit={editing.it} deptKey={deptKey} canAddToDept={canAddToDept} canAddGlobal={canAddGlobal}
          onClose={() => setEditing(null)} onCreated={() => { setEditing(null); onChanged(); }} />
      )}
      {ctx && (
        <AppEmbedContextMenu ctx={ctx} onClose={() => setCtx(null)}
          onOpen={() => onSelect(appTabKey(ctx.it.id))}
          onEdit={ctx.it.can_edit ? () => setEditing({ it: ctx.it, x: ctx.x, y: ctx.y }) : undefined}
          onDelete={ctx.it.can_edit ? () => deleteItem(ctx.it) : undefined} />
      )}
    </div>
  );
}

/** Контекстное меню значка приложения (правый клик). Портал в body,
 *  позиция по курсору с зажимом в экран; закрывается кликом вне, Esc и скроллом. */
function AppEmbedContextMenu({
  ctx, onClose, onOpen, onEdit, onDelete,
}: {
  ctx: { it: AppEmbed; x: number; y: number };
  onClose: () => void;
  onOpen: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
}) {
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  useEffect(() => {
    const close = () => onClose();
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    // capture у скролла — ловим прокрутку любого контейнера, не только window.
    window.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
      window.removeEventListener("keydown", onKey);
    };
  }, [onClose]);
  if (!mounted) return null;

  const W = 208, ROWH = 34;
  const rows = 2 + (onEdit ? 1 : 0) + (onDelete ? 1 : 0);
  const H = rows * ROWH + 12;
  // Зажим в экран: у правого/нижнего края меню открывается влево/вверх от курсора.
  const x = Math.min(ctx.x, window.innerWidth - W - 8);
  const y = Math.min(ctx.y, window.innerHeight - H - 8);

  const run = (fn?: () => void) => () => { onClose(); fn?.(); };
  const item = "flex w-full items-center gap-2.5 rounded-[var(--radius-md)] px-2.5 py-2 text-left text-[12px] transition glass-hover";

  return createPortal((
    // Прозрачный слой — клик мимо закрывает; onContextMenu гасим, чтобы правый
    // клик по меню не открыл браузерное.
    <div className="fixed inset-0" style={{ zIndex: Z.MODAL }} onClick={onClose} onContextMenu={(e) => { e.preventDefault(); onClose(); }}>
      <div
        className="yuc-pop absolute flex flex-col gap-0.5 rounded-[var(--radius-lg)] p-1.5"
        style={{
          left: x, top: y, width: W,
          background: "var(--surfaceElevated, var(--surface))",
          border: "1px solid color-mix(in srgb, var(--text) 10%, transparent)",
          boxShadow: "0 18px 44px -12px rgba(0,0,0,0.45), inset 0 1px 0 color-mix(in srgb, #ffffff 8%, transparent)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2 px-2 pb-1 pt-0.5">
          <AppEmbedIcon item={ctx.it} size={20} />
          <span className="min-w-0">
            <span className="block truncate text-[11px] font-semibold text-[var(--text)]">{ctx.it.title}</span>
            <span className="block truncate text-[11px] text-[var(--textMuted)]">{ctx.it.provider_title}</span>
          </span>
        </div>
        <div className="my-0.5 h-px" style={{ background: "color-mix(in srgb, var(--text) 8%, transparent)" }} />
        <button type="button" className={item} onClick={run(onOpen)}>
          <Maximize2 size={14} className="text-[var(--textMuted)]" /> Открыть здесь
        </button>
        <a href={ctx.it.open_url} target="_blank" rel="noopener noreferrer" className={item} onClick={run()}>
          <ExternalLink size={14} className="text-[var(--textMuted)]" /> Открыть в сервисе
        </a>
        {onEdit && (
          <button type="button" className={item} onClick={run(onEdit)}>
            <Pencil size={14} className="text-[var(--textMuted)]" /> Изменить
          </button>
        )}
        {onDelete && (
          <button type="button" className={`${item} text-[var(--danger)]`} onClick={run(onDelete)}>
            <Trash2 size={14} /> Удалить
          </button>
        )}
      </div>
    </div>
  ), document.body);
}

/** Подпись приложения, РАЗВОРАЧИВАЮЩАЯСЯ из кружка свёрнутой полоски.
 *  Правый край пилюли ложится ровно на кружок, а свой значок она держит справа —
 *  так «обводка» выглядит продолжением, а не второй копией рядом. */
function AppEmbedHint({ it, top, right }: { it: AppEmbed; top: number; right: number }) {
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  if (!mounted) return null;
  return createPortal((
    <div className="pointer-events-none fixed -translate-y-1/2" style={{ zIndex: Z.MODAL, top, right }}>
      <div
        className="yuc-unfurl-left flex items-center gap-2 rounded-full py-1.5 pl-3.5 pr-1.5"
        style={{
          // Фон НЕПРОЗРАЧНЫЙ: --nav-aside-bg полупрозрачен (68%) — сквозь него
          // просвечивала страница и подпись не читалась.
          background: "var(--surface)",
          border: "1px solid color-mix(in srgb, var(--text) 10%, transparent)",
          boxShadow: "0 14px 34px -14px rgba(0,0,0,0.45), inset 0 1px 0 color-mix(in srgb, #ffffff 8%, transparent)",
        }}
      >
        <div className="text-right">
          <div className="whitespace-nowrap text-[11px] font-semibold leading-tight text-[var(--text)]">{it.title}</div>
          <div className="whitespace-nowrap text-[11px] leading-tight text-[var(--textMuted)]">{it.provider_title}</div>
        </div>
        <AppEmbedIcon item={it} size={26} />
      </div>
    </div>
  ), document.body);
}

// ── Модалка добавления / редактирования ─────────────────────────────────────
// Одна форма на оба случая (правка Олега 21.07 — «могу только удалить»): при
// редактировании ссылка НЕ обязательна, пустое поле = оставить прежнюю.
function AddAppModal({
  deptKey, canAddToDept, canAddGlobal, edit, anchor, onClose, onCreated,
}: {
  deptKey: string;
  canAddToDept: boolean;
  canAddGlobal: boolean;
  edit?: AppEmbed;
  /** Координаты клика — окно открывается ПОПАПОМ рядом с курсором, без затемнения. */
  anchor?: { x: number; y: number };
  onClose: () => void;
  onCreated: () => void;
}) {
  const [title, setTitle] = useState(edit?.title ?? "");
  const [code, setCode] = useState("");
  const [scope, setScope] = useState<AppEmbedScope>(edit?.scope ?? (canAddToDept ? "department" : "user"));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const ready = title.trim().length > 0 && (edit ? true : code.trim().length > 0);

  const submit = async () => {
    if (!ready || busy) return;
    setBusy(true); setErr("");
    try {
      if (edit) {
        await api.patchAppEmbed(edit.id, {
          title: title.trim(),
          scope,
          dept_key: scope === "department" ? (edit.dept_key || deptKey) : "",
          ...(code.trim() ? { code: code.trim() } : {}),
        });
      } else {
        await api.createAppEmbed({ title: title.trim(), code: code.trim(), scope, dept_key: deptKey });
      }
      onCreated();
    } catch (e) {
      // Бэкенд возвращает человеческую причину (не тот сервис / не нашёл ссылку).
      const raw = e instanceof Error ? e.message : (edit ? "Не удалось сохранить" : "Не удалось добавить");
      try { setErr(JSON.parse(raw).detail ?? raw); } catch { setErr(raw); }
    } finally { setBusy(false); }
  };

  // ⚠️ Модалку рендерим ПОРТАЛОМ в body: правая панель отдела несёт
  // `backdrop-filter`, а он (как и transform) делает предка containing block —
  // `position: fixed` начинает считаться от панели, и окно вжимается в её колонку.
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  if (!mounted) return null;

  // Попап у курсора (правка Олега 22.07): координаты из anchor, зажатые в экран.
  // Без затемнения — прозрачный слой ловит только клик мимо для закрытия.
  const W = 380, H = 520;
  const px = anchor ? Math.min(Math.max(8, anchor.x), (typeof window !== "undefined" ? window.innerWidth : 1200) - W - 8) : 0;
  const py = anchor ? Math.min(Math.max(8, anchor.y), (typeof window !== "undefined" ? window.innerHeight : 800) - H - 8) : 0;
  const positioned = !!anchor;
  const wrapCls = positioned
    ? "fixed inset-0 z-modal"
    : "fixed inset-0 z-modal flex items-center justify-center p-4";
  const panelCls = positioned
    ? "yuc-pop absolute w-[380px] max-w-[calc(100vw-16px)] rounded-[var(--radius-lg)] border border-[var(--border)] bg-[var(--surface)] p-4 elev-modal"
    : "relative w-full max-w-lg rounded-[var(--radius-lg)] border border-[var(--border)] bg-[var(--surface)] p-4 elev-modal";
  return createPortal((
    <div className={wrapCls} onClick={onClose}>
      {!positioned && <div className="absolute inset-0 bg-black/40" />}
      <div className={panelCls} style={positioned ? { left: px, top: py } : undefined} onClick={(e) => e.stopPropagation()}>
        <h2 className="text-[12px] font-bold text-[var(--text)]">
          {edit ? "Изменить приложение" : "Добавить приложение"}
        </h2>
        <p className="mt-1 text-[11px] leading-relaxed text-[var(--textMuted)]">
          {edit ? (
            <>
              Меняйте название, ссылку и то, кому приложение видно. Сейчас это <b>{edit.provider_title}</b>.
              Файл открывается <b>только на просмотр</b> — редактируют в самом сервисе.
            </>
          ) : (
            <>
              Вставьте ссылку на файл или код для вставки из сервиса (в Figma — Share → Get embed code).
              Поддерживаются Figma, Miro, Google Документы/Таблицы/Презентации/Формы/Календарь/Диск,
              YouTube и публичные страницы Notion. Файл открывается <b>только на просмотр</b> — редактируют в самом сервисе.
            </>
          )}
        </p>

        <label className="mt-3 block text-[11px] font-semibold text-[var(--textMuted)]">Название</label>
        <input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Например, «Оргсхема» или «Воронка продаж»"
          className="field mt-1 bg-[var(--bg)]"
        />

        <label className="mt-3 block text-[11px] font-semibold text-[var(--textMuted)]">
          Ссылка или код для вставки{edit ? " — оставьте пустым, если менять не нужно" : ""}
        </label>
        <textarea
          value={code}
          onChange={(e) => setCode(e.target.value)}
          rows={edit ? 3 : 4}
          placeholder={edit ? edit.open_url : 'https://miro.com/app/board/…  или  <iframe src="…"></iframe>'}
          className="field-sm mt-1 resize-y bg-[var(--bg)] font-mono field-area"
        />

        <div className="mt-3 text-[11px] font-semibold text-[var(--textMuted)]">Кому видно</div>
        {/* Тумблеры вместо выпадашки (правка Олега 21.07): варианта всего три,
            видеть их сразу удобнее, чем открывать список. Выбор взаимоисключающий —
            включение одного гасит остальные. Чекбоксы в портале не используем. */}
        <div className="mt-1.5 space-y-0.5">
          {(["department", "user", "global"] as AppEmbedScope[])
            .filter((sc) => (sc === "department" ? canAddToDept : true))
            .map((sc) => {
              const off = sc === "global" && !canAddGlobal;
              return (
                <div
                  key={sc}
                  onClick={() => { if (!off) setScope(sc); }}
                  className={`flex items-center gap-2 rounded-[var(--radius-md)] border px-2 py-1 transition ${off ? "opacity-50" : "cursor-pointer hover:bg-[var(--hover)]"}`}
                  style={{
                    borderColor: scope === sc ? "color-mix(in srgb, var(--primary) 55%, transparent)" : "var(--border)",
                    background: scope === sc ? "color-mix(in srgb, var(--primary) 8%, transparent)" : "transparent",
                  }}
                >
                  <Toggle on={scope === sc} disabled={off} onChange={() => { if (!off) setScope(sc); }} size="sm" />
                  {/* Подпись и пояснение в ОДНУ строку — окно и так высокое (правка Олега 21.07). */}
                  <div className="flex min-w-0 flex-1 items-baseline gap-1.5">
                    <span className="text-[11px] font-semibold text-[var(--text)]">{SCOPE_TITLE[sc]}</span>
                    <span className="truncate text-[11px] text-[var(--textMuted)]">
                      {SCOPE_HINT[sc]}{off ? " · добавляет только администратор" : ""}
                    </span>
                  </div>
                </div>
              );
            })}
        </div>

        {err && (
          <div className="mt-3 rounded-[var(--radius-md)] px-3 py-2 text-[11px]" style={{ color: "var(--danger)", background: "color-mix(in srgb, var(--danger) 10%, transparent)" }}>
            {err}
          </div>
        )}

        <div className="mt-4 flex items-center justify-end gap-2">
          <button type="button" onClick={onClose} className="rounded-[var(--radius-md)] px-3 py-2 text-[11px] text-[var(--textMuted)] glass-flat hover:text-[var(--text)]">
            Отмена
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={busy || !ready}
            className="glass-btn-primary inline-flex items-center gap-1.5 rounded-[var(--radius-md)] px-3 py-2 text-[11px] font-semibold disabled:opacity-50"
          >
            {busy ? <IconSpinner size={13} className="animate-spin" />
                  : edit ? <Check size={13} /> : <Plus size={13} />}
            {edit ? "Сохранить" : "Добавить"}
          </button>
        </div>
      </div>
    </div>
  ), document.body);
}

// ── Просмотр приложения в рабочей области ───────────────────────────────────
export function AppEmbedViewer({ item }: { item: AppEmbed }) {
  return (
    <div className="flex h-full min-h-0 flex-col gap-2 p-3">
      <div className="flex items-center gap-2">
        <AppEmbedIcon item={item} size={26} />
        <div className="min-w-0">
          <div className="section-title truncate">{item.title}</div>
          <div className="truncate text-[11px] text-[var(--textMuted)]">
            {item.provider_title} · только просмотр — редактировать в сервисе
          </div>
        </div>
        <a
          href={item.open_url}
          target="_blank"
          rel="noopener noreferrer"
          className="glass-pill ml-auto inline-flex items-center gap-1.5 rounded-[var(--radius-md)] border border-[var(--border)] px-2.5 py-1.5 text-[11px] text-[var(--text)] transition hover:bg-[var(--surface)]"
        >
          <ExternalLink size={13} /> Открыть в сервисе
        </a>
      </div>
      {/* sandbox — на случай, если сервис отдаст что-то неожиданное: скрипты
          сервиса нужны (без allow-scripts не работает ни Figma, ни Miro), но
          навигацию верхнего окна и доступ к нашему origin запрещаем.
          Проверено: sandbox сам по себе встраивание НЕ ломает. */}
      <iframe
        key={item.id}
        src={item.embed_url}
        title={item.title}
        className="min-h-0 w-full flex-1 rounded-[var(--radius-lg)] border border-[var(--border)] bg-[var(--bg)]"
        allow="fullscreen; clipboard-write"
        referrerPolicy="strict-origin-when-cross-origin"
        sandbox="allow-scripts allow-same-origin allow-popups allow-forms allow-downloads"
      />
      {/* ⚠️ Определить «сервис не дал встроиться» из кода НЕЛЬЗЯ: у кросс-доменного
          iframe onLoad недостоверен — на рабочем ролике YouTube он не сработал, а на
          битой ссылке Figma сработал (проверено). Поэтому не гадаем оверлеем поверх
          возможно рабочего файла, а даём постоянную спокойную подсказку под рамкой. */}
      <p className="shrink-0 px-1 text-[11px] leading-relaxed text-[var(--textMuted)]">
        Ничего не видно? Сервис мог запретить встраивание, или у вас нет доступа к файлу
        в самом сервисе — портал свою авторизацию не подставляет. Откройте в сервисе и проверьте доступ.
      </p>
    </div>
  );
}
